import React, { useCallback, useEffect, useRef, useState } from 'react';

export function useSiteHeader() {
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const servicesWrapRef = useRef<HTMLLIElement>(null);
  const servicesButtonRef = useRef<HTMLButtonElement>(null);
  const mobileButtonRef = useRef<HTMLButtonElement>(null);

  const toggleServices = useCallback(() => setServicesOpen((v) => !v), []);
  const closeServices = useCallback(() => setServicesOpen(false), []);

  const openMobile = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    window.setTimeout(() => mobileButtonRef.current?.focus(), 0);
  }, []);

  const navigateFromMobile = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    setMobileOpen(false);
    if (!href.startsWith('#')) return;
    e.preventDefault();
    window.setTimeout(() => {
      const target = document.querySelector(href);
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      window.history.replaceState(null, '', href);
    }, 60);
  }, []);

  const handleServicesBlur = useCallback((e: React.FocusEvent<HTMLLIElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setServicesOpen(false);
  }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (servicesOpen) {
        setServicesOpen(false);
        servicesButtonRef.current?.focus();
      }
      if (mobileOpen) closeMobile();
    }
    function onPointerDown(e: MouseEvent) {
      if (servicesOpen && servicesWrapRef.current && !servicesWrapRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [servicesOpen, mobileOpen, closeMobile]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => {
      if (mq.matches) setMobileOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return {
    servicesOpen,
    toggleServices,
    closeServices,
    servicesWrapRef,
    servicesButtonRef,
    handleServicesBlur,
    mobileOpen,
    openMobile,
    closeMobile,
    navigateFromMobile,
    mobileButtonRef,
  };
}
